/**
 * The icon set, inline.
 *
 * Replaces @carbon/icons-react. Twenty icons were in use; pulling a whole icon
 * library across the wire to draw twenty glyphs is a poor trade, and the set an
 * operations console needs does not grow quickly.
 *
 * All are 24×24, stroked rather than filled, and inherit `currentColor` — so an
 * icon takes the colour of whatever it sits in and never needs a colour prop.
 * `aria-hidden` is the default because an icon here always sits beside a label;
 * pass `title` on the rare occasion one stands alone, and it becomes an image
 * with an accessible name instead.
 */

import type { ReactNode } from 'react';

export interface IconProps {
  /** px or any CSS length. Number is treated as px, matching the old API. */
  size?: number | string;
  className?: string;
  /** Supplying this makes the icon exposed to assistive tech, not hidden. */
  title?: string;
}

function Svg({ size = 16, className, title, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}

/* ── navigation ──────────────────────────────────────────────────────────── */

export const DashboardIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="3" width="7" height="9" rx="1.5" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" />
    <rect x="14" y="12" width="7" height="9" rx="1.5" />
    <rect x="3" y="16" width="7" height="5" rx="1.5" />
  </Svg>
);

/** Reports: an axis with a rising line over it. */
export const ChartIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 3v16a2 2 0 0 0 2 2h15" />
    <path d="M7 15l4-5 3.5 3L20 6" />
  </Svg>
);

export const BuildingIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 21h18" />
    <path d="M5 21V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v15" />
    <path d="M15 21V11h3a2 2 0 0 1 2 2v8" />
    <path d="M9 8h2M9 12h2M9 16h2" />
  </Svg>
);

export const WarningIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10.3 3.9 1.9 18a2 2 0 0 0 1.7 3h16.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </Svg>
);

export const CompareIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 4h7v16H4z" />
    <path d="M13 8h7M13 12h7M13 16h7" />
  </Svg>
);

export const UserFollowIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M15 20v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1" />
    <circle cx="8.5" cy="7" r="3.5" />
    <path d="M17 9h5M19.5 6.5v5" />
  </Svg>
);

export const ServerDnsIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="3" width="18" height="7" rx="2" />
    <rect x="3" y="14" width="18" height="7" rx="2" />
    <path d="M7 6.5h.01M7 17.5h.01" />
  </Svg>
);

export const ChipIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="6" y="6" width="12" height="12" rx="2" />
    <path d="M9 1.5v3M15 1.5v3M9 19.5v3M15 19.5v3M1.5 9h3M1.5 15h3M19.5 9h3M19.5 15h3" />
  </Svg>
);

export const CatalogIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5Z" />
    <path d="M4 20.5A2.5 2.5 0 0 1 6.5 18H20v3H6.5A2.5 2.5 0 0 1 4 20.5Z" />
  </Svg>
);

export const UserAdminIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9" cy="7" r="3.5" />
    <path d="M2 20v-1a4 4 0 0 1 4-4h4" />
    <path d="M18 12.5 15 14v2.5c0 1.7 1.2 3.2 3 3.7 1.8-.5 3-2 3-3.7V14Z" />
  </Svg>
);

export const StethoscopeIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4.5 3H4a1.5 1.5 0 0 0-1.5 1.5V9a5.5 5.5 0 0 0 11 0V4.5A1.5 1.5 0 0 0 12 3h-.5" />
    <path d="M8 14.5V16a5 5 0 0 0 5 5 5 5 0 0 0 5-5v-3.6" />
    <circle cx="18" cy="9.5" r="2.4" />
  </Svg>
);

/* ── actions ─────────────────────────────────────────────────────────────── */

export const AddIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);

export const RenewIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20.5 12a8.5 8.5 0 1 1-2.6-6.1" />
    <path d="M20.5 4v5h-5" />
  </Svg>
);

export const TrashIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3.5 6h17" />
    <path d="M8 6V4.5A1.5 1.5 0 0 1 9.5 3h5A1.5 1.5 0 0 1 16 4.5V6" />
    <path d="M6 6v13.5A1.5 1.5 0 0 0 7.5 21h9a1.5 1.5 0 0 0 1.5-1.5V6" />
    <path d="M10 10.5v6M14 10.5v6" />
  </Svg>
);

export const CopyIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4.5A1.5 1.5 0 0 1 3 13.5v-9A1.5 1.5 0 0 1 4.5 3h9A1.5 1.5 0 0 1 15 4.5V5" />
  </Svg>
);

export const DownloadIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3v12" />
    <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
    <path d="M4 20h16" />
  </Svg>
);

export const LogoutIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10 21H5.5A1.5 1.5 0 0 1 4 19.5v-15A1.5 1.5 0 0 1 5.5 3H10" />
    <path d="M16 16.5 20.5 12 16 7.5" />
    <path d="M20 12H9" />
  </Svg>
);

export const TerminalIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2.5" y="4" width="19" height="16" rx="2" />
    <path d="m7 9.5 3 2.5-3 2.5" />
    <path d="M12.5 15h4.5" />
  </Svg>
);

export const SearchIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="m20 20-4.9-4.9" />
  </Svg>
);

export const CloseIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Svg>
);

export const MenuIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3.5 6.5h17M3.5 12h17M3.5 17.5h17" />
  </Svg>
);

export const ChevronRightIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m9 5 7 7-7 7" />
  </Svg>
);

export const ChevronDownIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m5 9 7 7 7-7" />
  </Svg>
);

export const ArrowUpIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 20V4M5.5 10.5 12 4l6.5 6.5" />
  </Svg>
);

export const ArrowDownIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 4v16M5.5 13.5 12 20l6.5-6.5" />
  </Svg>
);

export const ExternalIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 4h6v6" />
    <path d="M20 4 11 13" />
    <path d="M18 14v5.5A1.5 1.5 0 0 1 16.5 21h-11A1.5 1.5 0 0 1 4 19.5v-11A1.5 1.5 0 0 1 5.5 7H11" />
  </Svg>
);

/* ── status ──────────────────────────────────────────────────────────────────
   These four are filled, not stroked: a status mark is read at a glance and at
   small sizes, where a 1.75px stroke turns to mush. Their silhouettes differ on
   purpose — disc, triangle, square, hollow ring — so state survives greyscale,
   forced-colors, and the eight percent of men who will not see the hue.        */

export const CheckFilledIcon = ({ size = 16, className, title }: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden={title ? undefined : true}
    role={title ? 'img' : undefined}
    focusable="false"
  >
    {title ? <title>{title}</title> : null}
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1.2 14.3-4-4 1.4-1.4 2.6 2.6 5.6-5.6 1.4 1.4Z" />
  </svg>
);

export const WarningFilledIcon = ({ size = 16, className, title }: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden={title ? undefined : true}
    role={title ? 'img' : undefined}
    focusable="false"
  >
    {title ? <title>{title}</title> : null}
    <path d="M12.9 3.5a1 1 0 0 0-1.8 0L1.4 20.4A1 1 0 0 0 2.3 22h19.4a1 1 0 0 0 .9-1.6ZM11 9h2v6h-2Zm0 8h2v2h-2Z" />
  </svg>
);

export const ErrorFilledIcon = ({ size = 16, className, title }: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden={title ? undefined : true}
    role={title ? 'img' : undefined}
    focusable="false"
  >
    {title ? <title>{title}</title> : null}
    <path d="M7.8 2h8.4L22 7.8v8.4L16.2 22H7.8L2 16.2V7.8Zm3.2 5v7h2V7Zm0 9v2h2v-2Z" />
  </svg>
);

export const InfoFilledIcon = ({ size = 16, className, title }: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    aria-hidden={title ? undefined : true}
    role={title ? 'img' : undefined}
    focusable="false"
  >
    {title ? <title>{title}</title> : null}
    <circle cx="12" cy="12" r="9.5" />
    <path d="M12 11v6" strokeLinecap="round" />
    <path d="M12 7.5h.01" strokeLinecap="round" />
  </svg>
);
